import {
  CheckCircle,
  Refresh,
  Search
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  DataGrid,
  GridFooterContainer,
  GridPagination
} from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const PatientsTable = ({ currentRole, onOpenLead, onAuthExpired }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [diagnoses, setDiagnoses] = useState([]);
  const [remarks, setRemarks] = useState('');

  const diagnosisOptions = [
    'Advised Glasses',
    'Keratoconus',
    'Normal',
    'Refer To Hospital',
    'Squint',
    'Cataract',
    'Lasik',
    'Pterygium',
    'Retina'
  ];

  // Fetch leads from API
  const fetchLeads = useCallback(async (search = '') => {
    setLoading(true);
    console.log('Fetching leads - Page:', paginationModel.page + 1, 'PageSize:', paginationModel.pageSize);
    try {
      const token = localStorage.getItem('accessToken');
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(
        `https://camps.vasaneye.com/vec-camps-backend/leads/?per_page=${paginationModel.pageSize}&page=${paginationModel.page + 1}&filter=all${searchParam}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'x-vec-camp-session-token': token
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLeads(data.data || []);

        // Calculate total count for pagination
        // If there are more records, we need to estimate total
        // Otherwise, use the count from current page
        const currentCount = data.data?.length || 0;
        const currentPage = data.info?.page || 1;
        const totalLeads = data.info?.count || 0;
        const hasMoreRecords = data.info?.more_records || false;

        setTotalCount(totalLeads);
        console.log('Total leads from API:', totalLeads, 'Current count:', currentCount, 'Has more records:', hasMoreRecords);

        // if (hasMoreRecords) {
        //   // If there are more records, set a higher count to enable next page
        //   const estimatedTotal = currentPage * paginationModel.pageSize + 1;
        //   console.log('More records available. Setting total to:', estimatedTotal);
        //   setTotalCount(estimatedTotal);
        // } else {
        //   // If no more records, calculate exact total
        //   const exactTotal = (currentPage - 1) * paginationModel.pageSize + currentCount;
        //   console.log('No more records. Exact total:', exactTotal);
        //   setTotalCount(exactTotal);
        // }
      } else {
        console.error('Failed to fetch leads:', response.status);
        if (response.status === 401 || response.status === 403) {
          onAuthExpired && onAuthExpired();
        }
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  // Fetch leads when component mounts or pagination changes (not on every searchValue change)
  useEffect(() => {
    fetchLeads(searchValue);
    // eslint-disable-next-line
  }, [fetchLeads]);

  // Handle pagination change
  const handlePaginationChange = (newModel) => {
    console.log('Pagination changed:', newModel);
    setPaginationModel(newModel);
  };

  // Only update searchValue state, don't fetch on every keystroke
  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  // Trigger search when Enter button is clicked or Enter key is pressed
  const handleSearchSubmit = () => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    fetchLeads(searchValue);
  };


  const handleDiagnosisChange = (diagnosis) => {
    setDiagnoses(prev =>
      prev.includes(diagnosis)
        ? prev.filter(d => d !== diagnosis)
        : [...prev, diagnosis]
    );
  };

  const handleSave = () => {
    if (selectedPatient) {
      console.log('Updating patient:', selectedPatient.id, { diagnoses, remarks });
      setOpenDialog(false);
      setSelectedPatient(null);
      setDiagnoses([]);
      setRemarks('');
      fetchLeads(searchValue);
    }
  };

  // Responsive columns using flex and mobile-hide
  const columns = useMemo(() => [
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => onOpenLead && onOpenLead(params.row.id)}
          sx={{ backgroundColor: '#1e3a8a', color: 'white', textTransform: 'none', px: 1.5, py: 0.5 }}
        >
          View
        </Button>
      )
    },
    { field: 'first_name', headerName: 'First Name', flex: 1, minWidth: 100, renderCell: (p) => p.value || 'N/A' },
    { field: 'last_name', headerName: 'Last Name', flex: 1, minWidth: 100, renderCell: (p) => p.value || 'N/A' },
    !isMobile && {
      field: 'date_of_birth', headerName: 'Date of Birth', flex: 1, minWidth: 120,
      renderCell: (p) => p.value ? new Date(p.value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
    },
    { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 130, renderCell: (p) => p.value || 'N/A' },
    !isMobile && {
      field: 'description', headerName: 'Advised', flex: 1, minWidth: 150,
      renderCell: (p) => { const s = p.value || ''; return s ? s.substring(0, 35) + (s.length > 35 ? '...' : '') : 'N/A'; }
    },
    !isMobile && {
      field: 'service_comment', headerName: 'Service Comments', flex: 1, minWidth: 180,
      renderCell: (p) => { const s = p.value || ''; return s ? s.substring(0, 35) + (s.length > 35 ? '...' : '') : 'N/A'; }
    },
    {
      field: 'lead_status', headerName: 'Status', flex: 0.8, minWidth: 120,
      renderCell: (p) => (<Chip label={p.value ? 'Reviewed' : 'Not Reviewed'} color={p.value ? 'success' : 'warning'} size="small" />)
    }
  ].filter(Boolean), [isMobile, onOpenLead]);

  const CustomFooter = () => (
    <GridFooterContainer>
      <IconButton aria-label="Refresh" title="Refresh" onClick={() => fetchLeads(searchValue)} disabled={loading} sx={{ ml: 1 }}>
        <Refresh />
      </IconButton>
      <Box sx={{ flex: 1 }} />
      <GridPagination />
    </GridFooterContainer>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Box sx={{ maxWidth: 'xl', mx: 'auto', p: isMobile ? '2vh' : '5vh' }}>
        {/* Search Field */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            fullWidth={isMobile}
            placeholder="Search leads..."
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 360 },
              '& .MuiOutlinedInput-root': { borderRadius: '5px', backgroundColor: 'white' }
            }}
          />
          <Button variant="contained" onClick={handleSearchSubmit} sx={{ backgroundColor: '#1e3a8a', color: 'white', minHeight: '40px' }}>
            Search
          </Button>
        </Stack>

        <Paper sx={{ height: { xs: 500, sm: 600 }, width: '100%', borderRadius: '5px', overflowX: 'auto' }}>
          <DataGrid
            rows={leads}
            columns={columns}
            loading={loading}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            pageSizeOptions={isMobile ? [5, 10, 25] : [10, 25, 50]}
            rowCount={totalCount}
            disableSelectionOnClick
            autoHeight={false}
            hideFooterSelectedRowCount
            slots={{ footer: CustomFooter }}
            sx={{
              border: 0,
              borderRadius: '5px',
              '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' },
            }}
          />
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Patient Review - {selectedPatient ? `${selectedPatient.first_name || ''} ${selectedPatient.last_name || ''}`.trim() || 'N/A' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: { xs: '60vh', sm: '70vh' }, overflowY: 'auto' }}>
          {/* existing dialog content remains */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Patient Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Name:</Typography>
                <Typography variant="body1">
                  {selectedPatient ? `${selectedPatient.first_name || ''} ${selectedPatient.last_name || ''}`.trim() || 'N/A' : 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Age & Gender:</Typography>
                <Typography variant="body1">
                  {selectedPatient?.age || 'N/A'} years, {selectedPatient?.gender || 'N/A'}
                </Typography>
              </Box>
              {selectedPatient?.service_comment && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Service Comments:</Typography>
                  <Typography variant="body1">{selectedPatient.service_comment}</Typography>
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Created:</Typography>
                <Typography variant="body1">
                  {selectedPatient?.created_time ? new Date(selectedPatient.created_time).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Diagnoses
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {diagnosisOptions.map(option => (
                  <FormControlLabel
                    key={option}
                    control={<Checkbox checked={diagnoses.includes(option)} onChange={() => handleDiagnosisChange(option)} size="small" />}
                    label={option}
                    sx={{ display: 'block', mb: 0.5 }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Remarks
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter remarks..."
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<CheckCircle />} sx={{ backgroundColor: '#1e3a8a' }}>
            Save & Mark as Reviewed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsTable;
